/**
 * Animated equalizer logo for Spotivibly.
 * Vertical bars pulse inside letter shapes via SVG clipPath.
 */

import { useEffect, useId, useState } from "react";

/**
 * Track Archivo Black load state at module level so subsequent mounts
 * don't re-await it. document.fonts.ready resolves once per page; once
 * we know the font is available, all future LogoEqualizer renders skip
 * the brief invisible state.
 */
let archivoLoaded = false;

interface LogoVariantProps {
  className?: string;
  size?: "sm" | "lg";
}

interface EqConfig {
  barW: [number, number];      // [lg, sm]
  barGap: [number, number];
  barFill: string;
  barOpacity: number;
  baseOpacity: number;
  barRx: [number, number];
  /** Speed multiplier — lower = faster */
  speed: number;
  /** Per-bar delay multiplier */
  stagger: number;
  /** Whether bars have a gradient (lighter at top) */
  gradient?: boolean;
}

const EqLogo = ({
  className = "",
  size = "sm",
  config,
}: LogoVariantProps & { config: EqConfig }) => {
  const uid = useId().replace(/:/g, "");
  const isLg = size === "lg";
  const idx = isLg ? 0 : 1;

  // SMIL <animate> ignores the prefers-reduced-motion media query (it's not
  // CSS), so gate the pulsing bars in JS. Reduced-motion users get the solid
  // wordmark — which is also what static captures (social embeds, screenshots)
  // show, so the brand reads identically there. (Design v2, 2026-07-05.)
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const fontSize = isLg ? 58 : 23;
  const svgHeight = isLg ? 70 : 28;
  const svgWidth = isLg ? 460 : 180;
  const textY = isLg ? 56 : 22;

  const barW = config.barW[idx];
  const barGap = config.barGap[idx];
  const numBars = Math.floor(svgWidth / (barW + barGap));

  const fontStyle: React.CSSProperties = {
    fontFamily: '"Archivo Black", system-ui, sans-serif',
    fontSize: `${fontSize}px`,
    fontWeight: 400,
    letterSpacing: "0.04em",
  };

  // Hide the SVG until Archivo Black is loaded. The clipPath relies on
  // exact letter shapes — painting with the system-ui fallback then
  // swapping to Archivo creates a visible bar-vs-letter misalignment
  // (reads as a glitch on mobile). Preload in index.html + this gate
  // means we usually skip straight to the font-loaded state. Falls
  // back to font-display:swap behavior if document.fonts is missing
  // (older browsers) — still better than nothing.
  //
  // 2026-05-22: added a hard 1s safety timeout + the actual text we
  // render ("SPOTIVIBLY") as the second arg to document.fonts.load().
  // Without the text arg, `load(size + 'px "Archivo Black"')` waits
  // for EVERY @font-face declared under that family. We declare two
  // (latin + latin-ext) in index.css for character-range splitting;
  // chrome-devtools mobile emulation observed the latin-ext face
  // stuck in `status: "unloaded"` while latin was `"loading"`, and
  // the promise never resolved → logo invisible on /generate
  // (`ready` stayed false, opacity 0). Passing "SPOTIVIBLY" makes
  // the load promise resolve once the face covering those exact
  // characters loads — latin-ext stays out of the wait. The 1s
  // timeout is defense-in-depth: if both faces somehow hang, the
  // logo still appears with the system-ui fallback. Pre-fix some
  // /generate visits showed no logo at all.
  const [ready, setReady] = useState(archivoLoaded);
  useEffect(() => {
    if (archivoLoaded) return;
    if (typeof document === "undefined" || !document.fonts?.load) {
      setReady(true);
      return;
    }
    let cancelled = false;
    // Hard 1s ceiling so a stuck font face can't strand the logo.
    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled) setReady(true);
    }, 1000);
    // Specifying the text matters — fonts.load() resolves once the
    // face covering THESE GLYPHS is ready, instead of waiting for
    // every @font-face under "Archivo Black".
    document.fonts
      .load(`${fontSize}px "Archivo Black"`, "SPOTIVIBLY")
      .then(() => {
        archivoLoaded = true;
        window.clearTimeout(fallbackTimer);
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        window.clearTimeout(fallbackTimer);
        // If loading errors out, render anyway — fallback is better
        // than no logo at all.
        if (!cancelled) setReady(true);
      });
    return () => { cancelled = true; window.clearTimeout(fallbackTimer); };
  }, [fontSize]);

  return (
    <div
      className={className}
      // Reserve space so the page doesn't reflow when the SVG appears
      // (CLS prevention). Width derived from viewBox aspect ratio.
      style={{ width: svgWidth, height: svgHeight, opacity: ready ? 1 : 0, transition: "opacity 120ms ease-out" }}
    >
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        height={svgHeight}
        aria-label="Spotivibly"
        role="img"
      >
        <defs>
          <clipPath id={`eq-${uid}`}>
            <text x="0" y={textY} style={fontStyle}>
              SPOTIVIBLY
            </text>
          </clipPath>
          {config.gradient && (
            <linearGradient id={`grad-${uid}`} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="hsl(142 70% 52%)" />
              <stop offset="100%" stopColor="hsl(142 65% 38%)" />
            </linearGradient>
          )}
        </defs>

        {/* Base text fill - always visible */}
        <text
          x="0"
          y={textY}
          fill="hsl(142 65% 38%)"
          opacity={config.baseOpacity}
          style={fontStyle}
        >
          SPOTIVIBLY
        </text>

        {/* Vertical bars clipped to text - pulse up/down. Hidden entirely for
            reduced-motion users (SMIL can't be gated via CSS media queries). */}
        {!reducedMotion && (
        <g clipPath={`url(#eq-${uid})`}>
          {Array.from({ length: numBars }).map((_, i) => {
            const dur = config.speed * (1.0 + (i % 7) * 0.12);
            const delay = i * config.stagger;

            const hLo = svgHeight * (0.15 + (i % 3) * 0.05);
            const hHi = svgHeight * (0.8 + (i % 5) * 0.04);
            const hM1 = svgHeight * (0.3 + (i % 4) * 0.08);
            const hM2 = svgHeight * (0.65 + (i % 3) * 0.06);
            const hM3 = svgHeight * (0.2 + (i % 5) * 0.05);
            const hM4 = svgHeight * (0.7 - (i % 4) * 0.04);
            const heights = [hLo, hHi, hM1, hM2, hM3, hM4, hLo];
            const yVals = heights.map((h) => svgHeight - h);

            return (
              <rect
                key={i}
                x={i * (barW + barGap)}
                width={barW}
                fill={config.gradient ? `url(#grad-${uid})` : config.barFill}
                opacity={config.barOpacity}
                rx={config.barRx[idx]}
              >
                <animate
                  attributeName="height"
                  values={heights.map((h) => h.toFixed(1)).join(";")}
                  dur={`${dur.toFixed(2)}s`}
                  repeatCount="indefinite"
                  begin={`${delay.toFixed(2)}s`}
                />
                <animate
                  attributeName="y"
                  values={yVals.map((y) => y.toFixed(1)).join(";")}
                  dur={`${dur.toFixed(2)}s`}
                  repeatCount="indefinite"
                  begin={`${delay.toFixed(2)}s`}
                />
              </rect>
            );
          })}
        </g>
        )}
      </svg>
    </div>
  );
};

/* ─── Final: Chunky Bright — wide bars, vivid green ───────────
 *
 * 2026-05-03 (revised): originally barOpacity 0.9 / baseOpacity 0.4
 * read as glitchy (letters half-missing when bars dipped). First fix
 * inverted to 0.45 / 0.92 — readable but flat, no animation feel.
 * Settled on 0.7 / 0.6.
 *
 * 2026-07-05 (design v2 audit): at 0.6 base the wordmark still reads
 * "glitch-styled" in any STATIC capture (screenshots, social embeds,
 * a fast first scan) — letters look half-eaten wherever a bar happens
 * to sit mid-pulse. Retuned to base 0.82 / bars 0.55: letterforms are
 * always solid (the brand mark never looks broken at rest), the
 * equalizer pulse reads as a shimmer *inside* confident letters rather
 * than the letters themselves flickering. The music-visualizer concept
 * stays — it's the most on-thesis wordmark this product could have.
 */
const configFinal: EqConfig = {
  barW: [6, 3],
  barGap: [3, 1.5],
  barFill: "hsl(142 76% 52%)",
  barOpacity: 0.55,
  baseOpacity: 0.82,
  barRx: [1.5, 0.5],
  speed: 1.1,
  stagger: 0.06,
};
export const LogoEqualizer = (p: LogoVariantProps) => (
  <EqLogo {...p} config={configFinal} />
);

